import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '@/lib/api/auth';
import { LogoSVG } from '@/design-system/LogoSVG';

/**
 * Tela pública de "definir senha", aberta pelo link de uso único que o
 * titular/admin disparou na tela de equipe. Mesmo layout de `LoginPage.tsx`
 * -- é a mesma porta de entrada do sistema, só que sem credencial ainda.
 *
 * O token é lido da URL (`?token=`) e nunca é reexibido nem armazenado; ele
 * viaja só no corpo da requisição de validação e de confirmação.
 */
function mensagemDeErro(err: any): string {
  const status = err?.response?.status;

  if (!err?.response) {
    return 'Não foi possível falar com o servidor. Verifique sua conexão.';
  }

  if (status === 400 || status === 401) {
    const msg = err.response.data?.message;
    if (typeof msg === 'string') return msg;
    if (Array.isArray(msg)) return msg.join(' · ');
    return 'Não foi possível definir a senha.';
  }

  return `Não foi possível concluir (erro ${status}).`;
}

export function DefinirSenhaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [checando, setChecando] = useState(true);
  const [valido, setValido] = useState(false);
  const [nome, setNome] = useState('');

  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [concluido, setConcluido] = useState(false);

  useEffect(() => {
    if (!token) {
      setChecando(false);
      setValido(false);
      return;
    }
    authApi
      .validarTokenDefinirSenha(token)
      .then((res) => {
        setValido(res.valido);
        setNome(res.nome || '');
      })
      .catch(() => setValido(false))
      .finally(() => setChecando(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (senha.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres.');
      return;
    }
    if (senha !== confirmacao) {
      setError('As senhas não conferem.');
      return;
    }

    setLoading(true);
    try {
      await authApi.definirSenha(token, senha);
      setLoading(false);
      setConcluido(true);
    } catch (err: any) {
      setLoading(false);
      setError(mensagemDeErro(err));
    }
  };

  return (
    <div className="login" role="main">
      <div className="l" aria-hidden="true">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
          <LogoSVG size={38} color="#F58406" segment="all" />
          <span className="wordmark" style={{ fontSize: 22 }}>Eleg360</span>
        </div>
        <div>
          <h1 style={{ marginTop: 24, fontSize: 36, lineHeight: 1.15 }}>
            Defina sua senha de acesso.
          </h1>
          <p style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
            Este link foi enviado pelo administrador do seu mandato e serve para um único uso.
          </p>
        </div>
        <div style={{ fontSize: 12, opacity: 0.55, marginTop: 'auto' }}>
          Multi-tenant · LGPD by design · Auditoria estrutural · PWA offline
        </div>
      </div>

      <div className="r">
        {checando ? (
          <p style={{ fontSize: 14, color: 'var(--ink3)' }}>Verificando o link...</p>
        ) : concluido ? (
          <div>
            <h2 style={{ fontSize: 20, marginBottom: 16 }}>Senha definida</h2>
            <p style={{ fontSize: 14, color: 'var(--ink2)', marginBottom: 16 }}>
              Sua senha foi definida com sucesso. Você já pode entrar com ela.
            </p>
            <button
              type="button"
              className="btn pri"
              style={{ width: '100%', padding: '11px', fontSize: 14 }}
              onClick={() => navigate('/login', { replace: true })}
            >
              Ir para o login
            </button>
          </div>
        ) : !valido ? (
          <div>
            <h2 style={{ fontSize: 20, marginBottom: 16 }}>Link inválido ou expirado</h2>
            <p style={{ fontSize: 14, color: 'var(--ink2)' }}>
              Este link já foi usado, expirou ou não é válido. Peça um novo link ao
              administrador do seu mandato.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate aria-label="Formulário de definição de senha">
            <h2 style={{ fontSize: 20, marginBottom: 16 }}>
              {nome ? `Olá, ${nome}. Defina sua senha` : 'Defina sua senha'}
            </h2>

            <div className="field">
              <label htmlFor="nova-senha">Nova senha</label>
              <input
                id="nova-senha"
                type="password"
                className="in"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                autoComplete="new-password"
                autoFocus
                required
              />
            </div>
            <div className="field">
              <label htmlFor="confirmacao-senha">Confirme a senha</label>
              <input
                id="confirmacao-senha"
                type="password"
                className="in"
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>

            {error && (
              <div className="note crit" role="alert" style={{ marginBottom: 12 }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn pri"
              disabled={loading}
              style={{ width: '100%', padding: '11px', fontSize: 14 }}
            >
              {loading ? 'Aguarde...' : 'Definir senha'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
